import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

const MAX_COMMENT_LENGTH = 1000

/**
 * Calificación por resumen individual (pedido de Nacho) — distinta del
 * módulo genérico BetaFeedback (ese califica la FEATURE en general; esto
 * califica ESTE resumen puntual). Inline, sin modal, nunca obligatoria: los
 * thumbs se mandan solos con un click; el comentario es un paso EXTRA
 * opcional detrás de un link, nunca forzado.
 */
export function HceSummaryRating({ rating: initialRating, onRate }) {
  const { t } = useTranslation()
  const [rating, setRating] = useState(initialRating ?? null)
  const [busy, setBusy] = useState(false)
  const [showComment, setShowComment] = useState(false)
  const [comment, setComment] = useState('')
  const [commentSent, setCommentSent] = useState(false)
  // Bug real (revisión 2026-08-11, mismo patrón que useClipboard en esta
  // misma feature): sin este guard, cambiar de paciente o cerrar el panel
  // mientras onRate() está en vuelo (ej. click en 👍 justo antes de
  // cerrar) dispara un setState real sobre una instancia ya desmontada —
  // mismo mountedRef/safeSetState ya usado en Print.js.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])
  const safeSetState = (setter, value) => { if (mountedRef.current) setter(value) }

  const submitRating = async value => {
    if (busy) return
    setBusy(true)
    try {
      await onRate(value)
      safeSetState(setRating, value)
    } catch {
      // Silencioso a propósito — es una calificación opcional, no hay nada
      // que interrumpirle al profesional con un error por esto.
    } finally {
      safeSetState(setBusy, false)
    }
  }

  const submitComment = async () => {
    if (busy || !comment.trim()) return
    setBusy(true)
    try {
      await onRate(rating, comment.trim())
      safeSetState(setCommentSent, true)
    } catch {
      // idem submitRating
    } finally {
      safeSetState(setBusy, false)
    }
  }

  return (
    <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
      <small className="text-muted">{t('hce_summary_rating_prompt', '¿Te sirvió este resumen?')}</small>
      <button
        type="button"
        aria-label={t('hce_summary_rating_up', 'Sí, me sirvió')}
        aria-pressed={rating === 1}
        className={`btn btn-sm ${rating === 1 ? 'btn-success' : 'btn-outline-secondary'}`}
        disabled={busy}
        onClick={() => submitRating(1)}
      >
        👍
      </button>
      <button
        type="button"
        aria-label={t('hce_summary_rating_down', 'No, no me sirvió')}
        aria-pressed={rating === -1}
        className={`btn btn-sm ${rating === -1 ? 'btn-danger' : 'btn-outline-secondary'}`}
        disabled={busy}
        onClick={() => submitRating(-1)}
      >
        👎
      </button>
      {rating != null && !showComment && !commentSent && (
        <button type="button" className="btn btn-sm btn-link p-0" onClick={() => setShowComment(true)}>
          {t('hce_summary_rating_add_comment', '+ comentario')}
        </button>
      )}
      {rating != null && showComment && !commentSent && (
        <>
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ maxWidth: '240px' }}
            value={comment}
            onChange={event => setComment(event.target.value)}
            maxLength={MAX_COMMENT_LENGTH}
            placeholder={t('hce_summary_rating_comment_placeholder', 'Contanos por qué (opcional)')}
          />
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy || !comment.trim()} onClick={submitComment}>
            {t('send', 'Enviar')}
          </button>
        </>
      )}
      {commentSent && <small className="text-success">{t('hce_summary_rating_thanks', '¡Gracias por tu feedback!')}</small>}
    </div>
  )
}

export default HceSummaryRating
